import { Modal, Tag } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import moment from "moment";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useState } from "react";

const DuplicateSupplyRequestWarningModal = ({
  duplicatedSupplyRequest,
  showDuplicateSupplyRequestError,
  setShowDuplicateSupplyRequestError,
  handleCreateSupplyOrder,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const renderLabelAndValue = (label, value) => {
    return (
      <div className="grid grid-cols-7 space-y-2">
        <p className="col-span-2 pt-2">{label}:</p>
        <Tag className="col-span-5 w-fit text-[.8rem]">{value}</Tag>
      </div>
    );
  };

  const handleSubmit = () => {
    setIsSubmitting(true);
    const submitTimeout = setTimeout(() => {
      handleCreateSupplyOrder();
      setIsSubmitting(false);
      setShowDuplicateSupplyRequestError(false);
    }, 1000);

    return () => clearTimeout(submitTimeout);
  };

  return (
    <Modal
      title={
        <div className="flex gap-2 mb-2">
          <WarningOutlined className="text-yellow-500" />
          <p>Duplicate Supply Request Detected</p>
        </div>
      }
      open={showDuplicateSupplyRequestError}
      centered
      width={650}
      okButtonProps={{
        className: "submitBtn",
        type: "primary",
        loading: isSubmitting,
      }}
      closable={false}
      cancelText="I will change"
      okText="Proceed anyway"
      onCancel={() => setShowDuplicateSupplyRequestError(false)}
      onOk={handleSubmit}
    >
      <div
        className="overflow-y-auto mb-10"
        style={{ maxHeight: "calc(100vh - 300px)" }}
      >
        <div className="mt-4 mb-2 flex items-start justify-between flex-wrap">
          <div className="flex items-start gap-2 flex-col">
            <p>
              Client Name: <Tag>{duplicatedSupplyRequest?.client}</Tag>
            </p>
            <p>
              Date Created:{" "}
              <Tag>
                {moment(duplicatedSupplyRequest?.dateCreated).format("LL")}
              </Tag>
            </p>
            <p>
              Created By: <Tag>{duplicatedSupplyRequest?.createdBy}</Tag>
            </p>
            <p>
              Total Requested Items:{" "}
              <Tag>{duplicatedSupplyRequest?.requestedItems?.length}</Tag>
            </p>
          </div>
          <div className="flex items-start gap-2 flex-col">
            <p>
              Order Status:{" "}
              {<Tag>{duplicatedSupplyRequest?.supplyRequestStatus}</Tag>}
            </p>
            <p>
              Requested By: <Tag>{duplicatedSupplyRequest?.requestedBy}</Tag>
            </p>
            <p>
              Request Source:{" "}
              <Tag>{duplicatedSupplyRequest?.requestSource}</Tag>
            </p>
          </div>
        </div>
        {duplicatedSupplyRequest?.requestedItems?.map((item) => (
          <Accordion key={item.id}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`panel${item?.id}-content`}
              id={item?.id}
              className="font-bold flex items-center gap-10"
            >
              <span className="mr-1 font-bold">
                {item?.inventoryItem?.name}
              </span>
              <span className="mr-1">-</span>
              <Tag color="success">{item?.quantityRequested}</Tag>
              <Tag className="ml-auto">{`${
                item?.supplyOrderStatus === "Released"
                  ? `Released on ${moment(item?.scheduledDeliveryDate).format(
                      "LL"
                    )}`
                  : item?.supplyOrderStatus
              }`}</Tag>
            </AccordionSummary>
            <hr />
            <AccordionDetails className="mt-2">
              {renderLabelAndValue(
                "Quantity Requested",
                item?.quantityRequested
              )}
              {item?.quantityIssued !== 0 &&
                renderLabelAndValue("Quantity Issued", item?.quantityIssued)}
              {item?.supplyOrderStatus === "Approved" &&
                renderLabelAndValue("Approved By", item?.approvedBy)}
              {renderLabelAndValue(
                "Date Created",
                moment(item?.dateCreated).format("LL")
              )}
              {renderLabelAndValue(
                "Category",
                item?.inventoryItem?.categoryName
              )}
              {renderLabelAndValue(
                "Supply Order Status",
                item?.supplyOrderStatus
              )}
              {item?.courierFullName !== null &&
                renderLabelAndValue("Courier", item?.courierFullName)}
              {item?.expirationDate !== null &&
                renderLabelAndValue(
                  "Expiration Date",
                  moment(item?.expirationDate).format("LL")
                )}
              {item?.scheduledDeliveryDate !== null &&
                renderLabelAndValue(
                  "Scheduled Delivery Date",
                  moment(item?.scheduledDeliveryDate).format("LL")
                )}
              {item?.supplyOrderStatus === "Rejected" &&
                renderLabelAndValue("Reject Reason", item?.rejectReason)}
              {item?.purpose && renderLabelAndValue("Purpose", item?.purpose)}
            </AccordionDetails>
          </Accordion>
        ))}
      </div>
    </Modal>
  );
};

export default DuplicateSupplyRequestWarningModal;
